import styled from "styled-components";

import {
	Text,
	Button,
	ColorV2,
	InputPrice,
	Tabs,
	ProgressBar,
} from "@adoptaunabuelo/react-components";
import ResumeStyled from "../../../components/container/ResumeContainer";
import { useEffect, useState } from "react";
import { State } from "../../../redux";
import { useSelector } from "react-redux";
import ReliefModal from "../../../components/modal/ReliefModal";
import media from "styled-media-query";

const Header = styled.div`
	background-color: ${ColorV2.surface.background};
	padding: 24px 24px 16px;
	${media.lessThan("medium")`
		padding: 32px 24px 16px;
    `}
`;
const DataView = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 24px;
	${media.lessThan("medium")`
		padding: 24px 24px 32px;
    `}
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const InputView = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	justify-content: center;
`;

const Resume = (props: Props) => {
	const appLocation = useSelector((state: State) => state.location.location);
	const config = useSelector((state: State) => state.location.config);
	const isMobile = useSelector((state: State) => state.screen.isMobile);

	const [priceOptions, setPriceOptions] = useState<number[]>([]);
	const [defaultPrice, setDefaultPrice] = useState<number>(0);
	const [conversion, setConversion] = useState<number>(1);
	const [selectedPrice, setSelectedPrice] = useState(15);
	const [selectedCharge, setSelectedCharge] = useState("monthly");
	const [reliefAmount, setReliefAmount] = useState("0");
	const [showReliefModal, setShowReliefModal] = useState(false);
	const [nActiveSubs, setNActiveSubs] = useState(0);

	useEffect(() => {
		getActiveSubscriptions();
	}, []);

	useEffect(() => {
		const temp1 = getOptionsPrice(selectedCharge);
		setPriceOptions(temp1);
		const temp2 = getDefaultPrice(selectedCharge);
		setDefaultPrice(temp2);
		setSelectedPrice(temp2);
		const temp3 = getConversionPrice();
		setConversion(temp3);
		props.onPriceChange &&
			props.onPriceChange(selectedPrice, selectedCharge);
	}, [selectedCharge, appLocation]);

	useEffect(() => {
		getReliefAmount(selectedPrice);
		props.onPriceChange &&
			props.onPriceChange(selectedPrice, selectedCharge);
	}, [selectedPrice, appLocation]);

	const getActiveSubscriptions = async () => {
		if (
			config &&
			config.attributes &&
			config.attributes.N_ACTIVE_SUBSCRIPTIONS
		) {
			setNActiveSubs(config.attributes.N_ACTIVE_SUBSCRIPTIONS.amount);
		}
	};

	const getReliefAmount = (price: number) => {
		const reliefSection1 = price > 250 ? 250 : price;
		const reliefSection2 = price > 250 ? price - 250 : 0;
		const relief = reliefSection1 * 0.8 + reliefSection2 * 0.4;
		setReliefAmount(relief.toFixed(2));
	};

	const getDefaultPrice = (charge: string) => {
		if (charge === "monthly") {
			return appLocation
				? appLocation.currency.price.monthly.default
				: 15;
		} else {
			return appLocation
				? appLocation.currency.price.annually.default
				: 150;
		}
	};

	const getOptionsPrice = (charge: string) => {
		if (charge === "monthly") {
			return appLocation &&
				appLocation.currency.price.monthly.inputOptions
				? appLocation.currency.price.monthly.inputOptions
				: [9, 15, 20];
		} else {
			return appLocation &&
				appLocation.currency.price.annually.inputOptions
				? appLocation.currency.price.annually.inputOptions
				: [90, 150, 250];
		}
	};

	const getConversionPrice = () => {
		return appLocation ? appLocation.currency.conversion : 1;
	};

	return (
		<ResumeStyled
			style={{
				padding: 0,
				overflow: "hidden",
				minWidth: isMobile ? "unset" : 420,
				marginBottom: isMobile ? -24 : 0,
				zIndex: 1,
				...props.style,
			}}
			childrenProps={{
				style: { flex: 1 },
			}}
		>
			<ReliefModal
				isVisible={showReliefModal}
				price={selectedPrice}
				charge={selectedCharge}
				onClose={() => setShowReliefModal(false)}
			/>
			<Header>
				<Text type="h4" weight="semibold" style={{ marginBottom: 32 }}>
					Hazte embajador para que ningún abuelo esté solo
				</Text>
				{nActiveSubs > 0 && (
					<ProgressBar
						progress={[
							{
								value: nActiveSubs,
							},
						]}
						maxValue={7500}
						showPercentage={true}
						style={{ marginBottom: 8 }}
					/>
				)}

				<Text
					type="h4"
					style={{ color: ColorV2.text.primary }}
					weight="semibold"
				>
					Ya somos{" "}
					{nActiveSubs.toLocaleString("es-ES", {
						useGrouping: true,
					})}{" "}
					embajadores
				</Text>
				<Text type="p2" style={{ color: ColorV2.text.neutralMedium }}>
					Necesitamos ser 7.500 para continuar
				</Text>
			</Header>
			<DataView>
				<InputView>
					<Tabs
						style={{ width: "unset", marginBottom: 24 }}
						design={"secondary"}
						textColor={ColorV2.text.neutralHard}
						cellColor={ColorV2.surface.neutralSoft}
						options={[
							{
								id: "monthly",
								title: "Mensual",
							},
							{
								id: "annually",
								title: "Anual",
							},
						]}
						onChange={(op) => {
							setSelectedCharge(op.id);
						}}
					/>
					<InputPrice
						style={{ marginBottom: 32 }}
						currency={
							appLocation ? appLocation.currency.symbol : "€"
						}
						options={priceOptions}
						defaultOption={defaultPrice}
						label={
							"Acompañaremos a un abuelo durante {{value}} horas"
						}
						labelValueConversion={0.8 * conversion}
						hideCustomAmount={true}
						onChange={(price) => setSelectedPrice(price)}
					/>
				</InputView>
				{appLocation &&
					appLocation.country_code === "ES" &&
					appLocation.currency.name === "eur" && (
						<div style={{ marginBottom: isMobile ? 0 : 24 }}>
							<Text
								type="c2"
								style={{
									textAlign: "center",
								}}
							>
								Te devolverán{" "}
								<span
									style={{
										backgroundColor:
											ColorV2.surface.secondary,
										padding: "1px 6px",
										borderRadius: 3,
										fontWeight: 500,
									}}
								>
									{reliefAmount.split(".")[0]}
									<span style={{ fontSize: 10 }}>
										{"," + reliefAmount.split(".")[1]} €
									</span>
								</span>{" "}
								en la Declaración de la Renta
							</Text>
							<Text
								type="c2"
								style={{
									textAlign: "center",
									marginTop: 4,
									textDecoration: "underline",
									cursor: "pointer",
								}}
								onClick={() => setShowReliefModal(true)}
							>
								Saber más
							</Text>
						</div>
					)}
				<Col
					style={{
						gap: 8,
						display: isMobile ? "none" : "flex",
					}}
				>
					<Button loading={props.loading} onClick={props.onClick}>
						Quiero donar{" "}
						{selectedPrice.toLocaleString("es-ES", {
							useGrouping: true,
						})}{" "}
						{appLocation ? appLocation.currency.symbol : "€"}{" "}
						{selectedCharge === "monthly"
							? "al mes"
							: selectedCharge === "annually"
							? "al año"
							: ""}
					</Button>
				</Col>
			</DataView>
		</ResumeStyled>
	);
};
export default Resume;
export interface Props {
	style?: React.CSSProperties;
	loading: boolean;
	onClick: () => void;
	onPriceChange?: (price: number, charge: string) => void;
	onShareClick: (op: { id: string }) => void;
}
